"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AlertTriangle, RefreshCw, Home, Wrench } from "lucide-react"
import Link from "next/link"
import { logger } from "@/lib/logger"
import { performanceAnalytics } from "@/lib/analytics"
import {
  claimChunkReload,
  isChunkLoadError,
  reloadWithCacheBuster,
} from "@/lib/chunk-error"

interface ToolsErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function ToolsError({ error, reset }: ToolsErrorProps) {
  const [recovering, setRecovering] = useState(false)

  useEffect(() => {
    const chunkError = isChunkLoadError(error)
    // A failed chunk fetch cannot be fixed by reset(): it re-runs the same
    // rejected import, so the only in-place recovery is a fresh network
    // request via a cache-busted reload.
    let recovery: "reload" | "exhausted" | undefined

    if (chunkError) {
      recovery = claimChunkReload("tools_error_boundary") ? "reload" : "exhausted"
    }

    logger.error("Tools page error", error, {
      digest: error.digest,
      page: "tools",
      chunkError,
      recovery
    })

    // Also surface the crash in PostHog Error Tracking, not just our logs.
    performanceAnalytics.captureError({
      error,
      source: "calculator",
      severity: "error",
      context: {
        digest: error.digest,
        page: "tools",
        chunkError,
        recovery
      }
    })

    if (recovery === "reload") {
      setRecovering(true)
      reloadWithCacheBuster()
    }
  }, [error])

  if (recovering) return null

  return (
    <div className="min-h-screen bg-linear-to-b from-green-50/50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-xl border-orange-100">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
          <CardTitle className="text-xl font-bold text-gray-900">
            Tool Temporarily Unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">
            We encountered an issue loading this lawn care tool. This is usually temporary and trying again often helps.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={reset}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>

            <Button
              asChild
              variant="outline"
              className="flex-1"
            >
              <Link href="/tools">
                <Wrench className="w-4 h-4 mr-2" />
                All Tools
              </Link>
            </Button>
          </div>

          <Button
            asChild
            variant="ghost"
            className="w-full text-gray-500 hover:text-gray-700"
          >
            <Link href="/">
              <Home className="w-4 h-4 mr-2" />
              Go to Home
            </Link>
          </Button>

          {process.env.NODE_ENV === "development" && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Error Details (Development Only)
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-48">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.digest && (
                  <div className="mb-2">
                    <strong>Digest:</strong> {error.digest}
                  </div>
                )}
                {error.stack && (
                  <div>
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1 text-xs">
                      {error.stack.slice(0, 500)}...
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
